import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Shield, AlertTriangle, Save, Lock, Unlock } from 'lucide-react';
import RichTextEditor from '../../../components/common/RichTextEditor';

export function InsuranceSettings({ data, setData, isSaving, onSave }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">Insurance Settings</h3>
          <p className="text-sm text-slate-500 mt-1">Configure mandatory insurance for bookings</p>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="insurance-enabled" className="text-sm">Enable Insurance</Label>
          <Switch
            id="insurance-enabled"
            checked={data.enabled}
            onCheckedChange={(checked) => setData(prev => ({ ...prev, enabled: checked }))}
            data-testid="insurance-enabled-switch"
          />
        </div>
      </div>

      <div className="space-y-8">
        {/* Customer Service Guarantee Insurance */}
        <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900">Customer Service Guarantee</h4>
                <p className="text-xs text-blue-700">Protection for customers against service issues</p>
              </div>
            </div>
            <Switch
              checked={data.customer_insurance_enabled}
              onCheckedChange={(checked) => setData(prev => ({ ...prev, customer_insurance_enabled: checked }))}
              data-testid="customer-insurance-switch"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 p-3 rounded-lg border border-blue-200 bg-blue-100/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {data.customer_insurance_mandatory ? <Lock className="w-4 h-4 text-blue-700" /> : <Unlock className="w-4 h-4 text-blue-500" />}
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      {data.customer_insurance_mandatory ? 'Mandatory' : 'Optional'} for Customers
                    </p>
                    <p className="text-xs text-blue-600">
                      {data.customer_insurance_mandatory 
                        ? 'Insurance is automatically included in all bookings' 
                        : 'Customers can choose to add insurance during booking'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={data.customer_insurance_mandatory}
                  onCheckedChange={(checked) => setData(prev => ({ ...prev, customer_insurance_mandatory: checked }))}
                  data-testid="customer-insurance-mandatory-switch"
                />
              </div>
            </div>
            <div>
              <Label className="text-blue-900">Pricing Type</Label>
              <select
                value={data.customer_pricing_type}
                onChange={(e) => setData(prev => ({ ...prev, customer_pricing_type: e.target.value }))}
                className="w-full mt-1 h-10 px-3 rounded-md border border-blue-300 bg-white text-sm"
              >
                <option value="percentage">Percentage of Booking</option>
                <option value="flat">Flat Fee</option>
              </select>
            </div>
            {data.customer_pricing_type === 'percentage' ? (
              <div>
                <Label className="text-blue-900">Percentage (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={data.customer_percentage}
                  onChange={(e) => setData(prev => ({ ...prev, customer_percentage: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-10"
                />
              </div>
            ) : (
              <div>
                <Label className="text-blue-900">Flat Fee (₱)</Label>
                <Input
                  type="number"
                  min="0"
                  value={data.customer_flat_fee}
                  onChange={(e) => setData(prev => ({ ...prev, customer_flat_fee: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-10"
                />
              </div>
            )}
            <div>
              <Label className="text-blue-900">Coverage Limit (₱)</Label>
              <Input
                type="number"
                min="0"
                value={data.customer_coverage_limit}
                onChange={(e) => setData(prev => ({ ...prev, customer_coverage_limit: parseFloat(e.target.value) || 0 }))}
                className="mt-1 h-10"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-blue-900">Coverage Includes</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <label className="flex items-center gap-2 text-sm text-blue-800">
                <input
                  type="checkbox"
                  checked={data.customer_covers_property_damage}
                  onChange={(e) => setData(prev => ({ ...prev, customer_covers_property_damage: e.target.checked }))}
                  className="rounded border-blue-300"
                />
                Property Damage
              </label>
              <label className="flex items-center gap-2 text-sm text-blue-800">
                <input
                  type="checkbox"
                  checked={data.customer_covers_incomplete_service}
                  onChange={(e) => setData(prev => ({ ...prev, customer_covers_incomplete_service: e.target.checked }))}
                  className="rounded border-blue-300"
                />
                Incomplete Service
              </label>
              <label className="flex items-center gap-2 text-sm text-blue-800">
                <input
                  type="checkbox"
                  checked={data.customer_covers_no_show}
                  onChange={(e) => setData(prev => ({ ...prev, customer_covers_no_show: e.target.checked }))}
                  className="rounded border-blue-300"
                />
                Provider No-Show
              </label>
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-blue-900">Terms & Conditions</Label>
            <div className="mt-1 insurance-terms-editor">
              <RichTextEditor
                value={data.customer_terms}
                onChange={(value) => setData(prev => ({ ...prev, customer_terms: value }))}
                placeholder="Describe what the customer insurance covers..."
                className="customer-terms-editor"
              />
            </div>
            <style>{`
              .insurance-terms-editor .ql-container {
                min-height: 150px;
                border-color: #93c5fd;
              }
              .insurance-terms-editor .ql-toolbar {
                border-color: #93c5fd;
              }
              .insurance-terms-editor .ql-editor {
                min-height: 130px;
              }
            `}</style>
          </div>
        </div>

        {/* Provider Accident Insurance */}
        <div className="p-5 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900">Provider Accident Insurance</h4>
                <p className="text-xs text-amber-700">Protection for providers against injuries during service</p>
              </div>
            </div>
            <Switch
              checked={data.provider_insurance_enabled}
              onCheckedChange={(checked) => setData(prev => ({ ...prev, provider_insurance_enabled: checked }))}
              data-testid="provider-insurance-switch"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 p-3 rounded-lg border border-amber-200 bg-amber-100/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {data.provider_insurance_mandatory ? <Lock className="w-4 h-4 text-amber-700" /> : <Unlock className="w-4 h-4 text-amber-500" />}
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      {data.provider_insurance_mandatory ? 'Mandatory' : 'Optional'} for Providers
                    </p>
                    <p className="text-xs text-amber-600">
                      {data.provider_insurance_mandatory 
                        ? 'Insurance is automatically applied to all provider bookings' 
                        : 'Providers see insurance as an informational benefit'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={data.provider_insurance_mandatory}
                  onCheckedChange={(checked) => setData(prev => ({ ...prev, provider_insurance_mandatory: checked }))}
                  data-testid="provider-insurance-mandatory-switch"
                />
              </div>
            </div>
            <div>
              <Label className="text-amber-900">Pricing Type</Label>
              <select
                value={data.provider_pricing_type}
                onChange={(e) => setData(prev => ({ ...prev, provider_pricing_type: e.target.value }))}
                className="w-full mt-1 h-10 px-3 rounded-md border border-amber-300 bg-white text-sm"
              >
                <option value="flat">Flat Fee per Booking</option>
                <option value="percentage">Percentage of Booking</option>
              </select>
            </div>
            {data.provider_pricing_type === 'percentage' ? (
              <div>
                <Label className="text-amber-900">Percentage (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={data.provider_percentage}
                  onChange={(e) => setData(prev => ({ ...prev, provider_percentage: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-10"
                />
              </div>
            ) : (
              <div>
                <Label className="text-amber-900">Flat Fee (₱)</Label>
                <Input
                  type="number"
                  min="0"
                  value={data.provider_flat_fee}
                  onChange={(e) => setData(prev => ({ ...prev, provider_flat_fee: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-10"
                />
              </div>
            )}
            <div>
              <Label className="text-amber-900">Coverage Limit (₱)</Label>
              <Input
                type="number"
                min="0"
                value={data.provider_coverage_limit}
                onChange={(e) => setData(prev => ({ ...prev, provider_coverage_limit: parseFloat(e.target.value) || 0 }))}
                className="mt-1 h-10"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-amber-900">Terms & Conditions</Label>
            <div className="mt-1 provider-terms-editor">
              <RichTextEditor
                value={data.provider_terms}
                onChange={(value) => setData(prev => ({ ...prev, provider_terms: value }))}
                placeholder="Describe what the provider insurance covers..."
                className="provider-terms-editor"
              />
            </div>
            <style>{`
              .provider-terms-editor .ql-container {
                min-height: 150px;
                border-color: #fcd34d;
              }
              .provider-terms-editor .ql-toolbar {
                border-color: #fcd34d;
              }
              .provider-terms-editor .ql-editor {
                min-height: 130px;
              }
            `}</style>
          </div>
        </div>

        {/* Info box about claims */}
        <div className="p-4 bg-stone-100 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-stone-600 flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-medium text-stone-900">Insurance Claims</h5>
              <p className="text-sm text-stone-600 mt-1">
                Insurance claims are handled through support tickets. When a customer or provider files a dispute related to covered incidents, 
                the admin team will review and process claims according to the terms specified above.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Button
        onClick={onSave}
        disabled={isSaving}
        className="mt-6 w-full sm:w-auto"
        data-testid="save-insurance-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Insurance Settings'}
      </Button>
    </div>
  );
}
